var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { apiCreateProfessionalSeminarClone, apiDeleteProfessionalSeminar, apiGetListProfessionalSeminarOrganization, apiGetListProfessionalSeminarSelf, } from '../../../actions/ProfessionalSeminarActions';
import { notify, wrapDispatch, wrapNotifyError } from '../../action';
import { useBoolState, useDidMount, usePromiseDoingCallback } from '../useHook';
/**
 * セミナーの読み込みを行うuse
 * @param dispatch
 * @param unmountRef
 * @returns {*}
 */
export const useProfessionalSeminarList = (dispatch, unmountRef) => {
    // 自分が作成したセミナーのページ
    const [selfPage, changeSelfPage] = useState(1);
    // 同組織のセミナーのページ
    const [organizationPage, changeOrganizationPage] = useState(1);
    // 選択されたセミナーID
    const [selectedSeminarId, changeSelectedSeminarId] = useState();
    const { organizationSeminarTotal, organizationSeminars: rawOrganizationSeminars, selfSeminarTotal, selfSeminars: rawSelfSeminars, } = useSelector((state) => state.professionalSeminar);
    // 削除モーダル制御
    const { boolState: isShowDeleteModal, changeFalse: hideDeleteModal, changeTrue: rawShowDeleteModal, } = useBoolState(false);
    // 複製モーダル制御
    const { boolState: isShowCloneModal, changeFalse: hideCloneModal, changeTrue: rawShowCloneModal, } = useBoolState(false);
    /**
     * ページ数の計算
     * @type {function(*): number}
     */
    const calcPageCount = useCallback((page) => {
        const value = Math.ceil(page / 10);
        return value === 0 ? 1 : value;
    }, []);
    // 自身が作成したセミナー
    const selfSeminars = rawSelfSeminars[selfPage] || [];
    const selfPageCount = useMemo(() => calcPageCount(selfSeminarTotal), [calcPageCount, selfSeminarTotal]);
    // 自身と同じ組織に所属するコンサルが作成したセミナー
    const organizationSeminars = rawOrganizationSeminars[organizationPage] || [];
    const organizationPageCount = useMemo(() => calcPageCount(organizationSeminarTotal), [calcPageCount, organizationSeminarTotal]);
    /**
     * 自身が作成したセミナーの読み込み
     * @type {(function(*=): Promise<void>)|*}
     */
    const rawLoadSelfSeminars = useCallback((setPage = null) => __awaiter(void 0, void 0, void 0, function* () {
        return wrapDispatch(dispatch, apiGetListProfessionalSeminarSelf({ page: setPage || selfPageCount }));
    }), [selfPageCount, dispatch]);
    /**
     * 自身と同じ組織に所属するコンサルが作成したセミナーの読み込み
     * @type {(function(*=): Promise<void>)|*}
     */
    const rawLoadOrganizationSeminars = useCallback((setPage = null) => __awaiter(void 0, void 0, void 0, function* () {
        return wrapDispatch(dispatch, apiGetListProfessionalSeminarOrganization({
            page: setPage || selfPageCount,
        }));
    }), [selfPageCount, dispatch]);
    // 自身が作成したセミナーの読み込み
    const [loadSelfSeminar, isSelfSeminarLoading] = usePromiseDoingCallback(rawLoadSelfSeminars, unmountRef);
    // 自身と同じ組織に所属するコンサルが作成したセミナーの読み込み
    const [loadOrganizationSeminar, isOrganizationSeminarLoading] = usePromiseDoingCallback(rawLoadOrganizationSeminars, unmountRef);
    /**
     * 削除確認モーダルを表示する
     * @type {(function(*))|*}
     */
    const showDeleteModal = useCallback((e) => {
        // 選択されたセミナーのIDを取得
        const { value } = e.currentTarget;
        changeSelectedSeminarId(parseInt(value, 10));
        // モーダルを表示
        rawShowDeleteModal();
    }, [rawShowDeleteModal]);
    /**
     * セミナーの削除を実行
     * @type {(function(*): Promise<void>)|*}
     */
    const rawOnDelete = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        // 選択されたセミナーのIDを取得
        const { value } = e.currentTarget;
        yield wrapNotifyError(() => __awaiter(void 0, void 0, void 0, function* () {
            // セミナーの削除を実行
            yield wrapDispatch(dispatch, apiDeleteProfessionalSeminar(value));
            if (unmountRef.current) {
                return;
            }
            // 自身が登録したセミナー一覧を再取得
            const { currentPage, lastPage } = yield rawLoadSelfSeminars(selfPage);
            if (unmountRef.current) {
                return;
            }
            if (lastPage < currentPage) {
                // 表示中のページにデータがない場合、先頭に戻す
                changeSelfPage(1);
            }
            // モーダルを閉じる
            hideDeleteModal();
            notify('セミナーを削除しました');
        }));
    }), [
        changeSelfPage,
        dispatch,
        hideDeleteModal,
        rawLoadSelfSeminars,
        selfPage,
        unmountRef,
    ]);
    const [onDelete, isDeleting] = usePromiseDoingCallback(rawOnDelete, unmountRef);
    /**
     * 複製確認モーダルを表示する
     * @type {(function(*))|*}
     */
    const showCloneModal = useCallback((e) => {
        // 選択されたセミナーのIDを取得
        const { value } = e.currentTarget;
        changeSelectedSeminarId(parseInt(value, 10));
        // モーダルを表示
        rawShowCloneModal();
    }, [rawShowCloneModal]);
    /**
     * セミナーの複製を実行
     * @type {(function(*): Promise<void>)|*}
     */
    const rawOnClone = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        // 選択されたセミナーのIDを取得
        const { value } = e.currentTarget;
        yield wrapNotifyError(() => __awaiter(void 0, void 0, void 0, function* () {
            // セミナーの複製を実行
            const { payload } = yield wrapDispatch(dispatch, apiCreateProfessionalSeminarClone(value));
            if (unmountRef.current) {
                return;
            }
            window.open(`/seminar/${payload.id}/edit`);
            // モーダルを閉じる
            hideCloneModal();
            // 自身が登録したセミナー一覧を再取得
            loadSelfSeminar(selfPage);
        }));
    }), [dispatch, hideCloneModal, loadSelfSeminar, selfPage, unmountRef]);
    const [onClone, isCloning] = usePromiseDoingCallback(rawOnClone, unmountRef);
    // 初期読み込みを実行
    useDidMount(() => {
        Promise.all([loadSelfSeminar(), loadOrganizationSeminar()]).then();
    });
    return {
        changeOrganizationPage,
        changeSelfPage,
        hideCloneModal,
        hideDeleteModal,
        isCloning,
        isDeleting,
        isOrganizationSeminarLoading,
        isSelfSeminarLoading,
        isShowCloneModal,
        isShowDeleteModal,
        loadOrganizationSeminar,
        loadSelfSeminar,
        onClone,
        onDelete,
        organizationPageCount,
        organizationSeminars,
        selectedSeminarId,
        selfPageCount,
        selfSeminars,
        showCloneModal,
        showDeleteModal,
    };
};
export default useProfessionalSeminarList;
